// src/pages/About.tsx
import { LockClosedIcon, Pencil1Icon, ResetIcon, TrashIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Lists } from '../../components/common/Lists';
import TableComponent from '../../components/common/Tables';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetUsersQuery } from '../../hooks/useGetUsersQuery';
import { setPdfPreviewType } from '../../redux/slices/report/report';
import { setUserData } from '../../redux/slices/users/user';
import { routes } from '../../routes';
import { changeUserLock } from '../../services/user/changeUserLock';
import { removeUser } from '../../services/user/removeUser';
import { resetPassword } from '../../services/user/resetPassword';
import { UserType } from '../../types/User';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { AlertDialog, Flex, Button, Badge } from '@radix-ui/themes';
import { ConfirmAlertDialog } from '../../components/common/ConfirmAlertDialog';
import { getUserClaims } from '../../services/user/getUserClaims';
import { getUserRoles } from '../../services/user/getUserRoles';
import { requestEmailPassword } from '../../services/forgotPassword/forgotPassword';
import { Key_users, PAGE_NUMBER, PAGE_SIZE } from '../../utils/constants';
import { useForm } from 'react-hook-form';
import { useGetRolesQuery } from '../../hooks/useGetRolesQuery';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import { getRoles } from '@testing-library/react';
import { useLogoBase64 } from '../../hooks/useLogoBase64';
import { createExportedDocument } from '../../services/school/student/createStudent';
import { useExportDocument } from '../../hooks/useExportDocument';
import { resetLoaderAvailable, setLoaderAvailable } from '../../redux/slices/loaderController';
import { useIES } from '../../hooks/useIES';

const columnNamesMap: { [key: string]: string } = {
    name: 'Nome Completo',
    email: 'E-mail',
    gender: 'Gênero',
    bi: 'Bilhete de Identidade',
    nuit: 'NUIT',
    dateOfBirth: 'Data de Nascimento',
    icon: 'Activo/Inactivo',
};


type TemplateData = {
  type: string;
  outputFormat?: string;
  data: Record<string, any>;
  logoBase64: string;
}

export const Users = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { UserId } = useSelector((state: RootState) => state.login.userData)
    const userData = useSelector((state: RootState) => state.login.userData);
    const {logoBase64} = useLogoBase64();
    const { exportDocumentPdf:exportDocument  } = useExportDocument();
    const { IES, isLoading } = useIES();
    const {
        data: roles,
        isLoading: isLoadingRoles,
        isError: isErrorRoles,
    } = useGetRolesQuery({ PageNumber: 1, PageSize: 400 });
    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue,
        trigger,
        watch,
        reset,
        resetField,
    } = useForm({
        defaultValues: { roleId: "" },
        mode: "onBlur",
        reValidateMode: "onBlur",
        shouldUseNativeValidation: false,
    });
    const { data, status, refetch } = useGetUsersQuery({ PageNumber: currentPage, PageSize: pageSize, SearchTerm: searchTerm, UserId: UserId, Role:getValues("roleId") });
    const queryClient = useQueryClient();
    const [lockUser, setLockUser] = useState<UserType | null>(null);
    const [resetUser, setResetUser] = useState<UserType | null>(null);
    const [sendLinkUser, setSendLinkUser] = useState<UserType | null>(null);
    const openDeleteDialog = (s: UserType) => {
        setLockUser(s);
    };
    const openResetDialog = (s: UserType) => {
        setResetUser(s);
    };
    const openSendLinkDialog = (s: UserType) => {
        setSendLinkUser(s);
    };

    const confirmLock = async () => {
        if (lockUser) {
            await handleChangeUserLock(lockUser.id!)
        }
    };
    const confirmReset = async () => {
        if (resetUser) {
            await handleResetPassword(resetUser!)
        }
    };
    const confirmSendLink = async () => {
        if (sendLinkUser) {
            await handleClickRecoverLinkPassword(sendLinkUser!)
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(PAGE_NUMBER);
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };
    const handleCreateItem = (event: any): void => {
        navigate(routes.createUser)
    }

    const handleUpdateItem = async (user: UserType) => {

        try {
            const claims = await getUserClaims(user.id!);
            const roles = await getUserRoles(user.id!);
            user.roles = roles;
            user.claims = claims;
            user.icon = null;
            user.roleId = roles?.at(0)?.name ?? "";
            user.roleIds = roles?.map(r=> r.name);
            if(user.email === ""){
                user.email = user.nuit + "@esura.com";
            }

            if(user.secondaryPhoneNumber === ""){
                user.secondaryPhoneNumber = undefined;
            }
            
           dispatch(setUserData(user));
           navigate(routes.updateUser);
        }
        catch {
            toast.error("Erro ao buscar dados do utilizador.");
        }
    };
    const mutationRequestEmailPassword = useMutation({
        mutationFn: requestEmailPassword, mutationKey: 'requestEmailPassword',
    });
    const handleClickRecoverLinkPassword = async (user: UserType) => {
        try {
            user.icon = null;
            await mutationRequestEmailPassword.mutateAsync(user);
            toast.success("Email de recuperação de senha enviado");
        } catch (err: any) {
            toast.error(`${mutationRequestEmailPassword.error ?? err?.message}`);
        }
    };
    const handleChangeUserLock = async (id: string) => {
        try {
            const result = await changeUserLock({ userId: id });
            if (result) {
                toast.success("utilizador [bloqueado/desbloqueado] com sucesso");
                queryClient.invalidateQueries(Key_users);
                navigate(routes.users);
            }
        } catch (err: any) {
            toast.error("Erro ao criar utilizador.");
        }
    };
    const handleUserRole = async (user: UserType) => {
        try {
            const claims = await getUserClaims(user.id!);
            const roles = await getUserRoles(user.id!);
            user.roles = roles;
            user.claims = claims;
            user.icon = null;
            dispatch(setUserData(user));
            navigate(routes.userRoleAndCLaims);
        }
        catch {
            toast.error("Erro ao buscar dados do utilizador.");
        }
    }
    
    const handleResetPassword = async (user: UserType) => {
        try {
            dispatch(setLoaderAvailable(true)); 
            const result = await resetPassword({ userId: user.id ?? "" });
            if (result) {
                toast.success("Senha de utilizador redefinida");
                user.password = result;
                const templateData: TemplateData = {
                    type: 'RESET_PASSWORD',
                    outputFormat: 'docx',
                    data: {
                        info:IES.NAME,
                        nome_completo: user.name,
                        documento_identificacao: user.bi,
                        senha: result,
                        utilizador: user.userName,
                        emitido_por: userData.Fullname,
                        data: new Date().toLocaleDateString(),
                    },
                    logoBase64: logoBase64??'',
                }
                await exportDocument(templateData, 'RESET_PASSWORD');
               return;
            }
        } catch (err: any) {
            toast.error("Erro ao imprimir o documento de senha do utilizador solicitado.");
        }finally{
           dispatch(resetLoaderAvailable()); 
        }
    };

    const options = data?.items.map((user: any) => [
        {
            label: 'Editar',
            onClick: (user: UserType) => handleUpdateItem(user),
            icon: <Pencil1Icon />
        },
        {
            label: 'Gerir perfil',
            onClick: (user: UserType) => handleUserRole(user),
            icon: <Pencil1Icon />
        },
        {
            label: 'Bloquear / Desbloquear',
            onClick: () => openDeleteDialog(user),
            icon: <LockClosedIcon />
        },
        {
            label: 'Redefinir senha',
            onClick: (user: UserType) => openResetDialog(user),
            icon: <ResetIcon />
        },
        {
            label: 'Enviar email de recuperação de senha',
            onClick: (user: UserType) => openSendLinkDialog(user),
            icon: <ResetIcon />
        },
    ]) || [];

    const handleRoleChange = (roleId: string) => {
        setValue("roleId", roleId);
        refetch();
    };
    return (
        <Flex direction={"column"} gap="4"  style={{ width: "100%" }}>
               <SelectComponent
                                name="roleId"
                                label="Perfil"
                                control={control}
                                errors={errors}
                                style={{ width: "100%" }}
                                options={roles?.items.map((role) => ({
                                    value: role.name ?? "",
                                    label: role.name ?? "",
                                }))}
                                onSelect={handleRoleChange}
                                itemsError={isErrorRoles}
                                itemsLoading={isLoadingRoles}
                            />
            <Lists
                status={status}
                TableComponent={() => <TableComponent
                    columns={Object.keys(columnNamesMap)}
                    items={data?.items.map(user => {
                        return {
                            ...user,
                            icon: !user.lockoutEnabled ? <Badge color="green">Activo </Badge> : <Badge color="red">Inactivo </Badge>
                        }
                    })}
                    options={options}
                    columnNamesMap={columnNamesMap}
                    totalPages={data?.totalPages || 1}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    onSearch={handleSearch}
                    search={searchTerm}
                    totalItems={data?.totalItems || 0}
                  
                />}
                handleClickCreate={handleCreateItem}

            />

            <ConfirmAlertDialog
                open={!!lockUser}
                onOpenChange={(open) => !open && setLockUser(null)}
                title="(Bloquear/Desbloquear utilizador)."
                item={lockUser}
                onConfirm={confirmLock}
                onCancel={() => setLockUser(null)}
            />
            <ConfirmAlertDialog
                open={!!resetUser}
                onOpenChange={(open) => !open && setResetUser(null)}
                title="(Redefinir senha) Esta operação é irreversível."
                item={resetUser}
                onConfirm={confirmReset}
                onCancel={() => setResetUser(null)}
            />
            <ConfirmAlertDialog
                open={!!sendLinkUser}
                onOpenChange={(open) => !open && setSendLinkUser(null)}
                title="(Enviar email de recuperação de senha) Esta operação é irreversível."
                item={sendLinkUser}
                onConfirm={confirmSendLink}
                onCancel={() => setSendLinkUser(null)}
            />


        </Flex>
    );
};

export default Users;